/**
 * Value types mirrored byte-for-byte from contracts/src/libraries/RunnerTypes.sol.
 * All monetary and positional values are BigInt; integer division truncates toward zero exactly like Solidity.
 */

export const EncounterKind = {
  NONE: 0,
  FORK: 1,
  BARRIER: 2,
  BURNING_BARRICADE: 3,
  WATER_STATION: 4,
  GOLDEN_SHOE: 5,
  FORGE_GATE: 6,
  FINISH: 7,
  MERGE: 8,
  /** Engine v2+: slows RUNNER 10 % for one step; no economic effect. */
  PUDDLE: 9,
} as const;
export type EncounterKindValue = (typeof EncounterKind)[keyof typeof EncounterKind];

export const EncounterName: Record<number, string> = {
  0: 'None',
  1: 'Fork',
  2: 'Barrier',
  3: 'Burning barricade',
  4: 'Water station',
  5: 'Golden Shoe',
  6: 'Forge gate',
  7: 'Finish',
  8: 'Merge',
  9: 'Puddle',
};

export const Route = { NEUTRAL: 0, FORGE: 1, HARVEST: 2 } as const;
export type RouteValue = (typeof Route)[keyof typeof Route];

export interface CourseObject {
  kind: number;
  y: bigint;
  xL: bigint;
  xR: bigint;
  param: bigint;
}

export interface FormationTemplate {
  length: bigint;
  mirrorable: boolean;
  maxShift: bigint;
  retired: boolean;
  objects: CourseObject[];
}

export interface MovementParams {
  basePace: bigint;
  paceBonusMax: bigint;
  paceBonusVolumePpm: bigint;
  xMax: bigint;
  halfWidth: bigint;
  uMax: bigint;
  sensitivity: bigint;
  decayPpm: bigint;
  vxMax: bigint;
  forkMedian: bigint;
  stumbleDistance: bigint;
}

export interface HandlerParams {
  barrierSlowPct: bigint;
  barrierSlowSteps: bigint;
  overheatSlowPct: bigint;
  overheatSteps: bigint;
  shoePrize: bigint;
  shoeMinPrize: bigint;
  forgeGateMax: bigint;
  forgeAuthSeconds: bigint;
  finishReleasesDeferred: boolean;
}

/** How a run's discretionary road budget is funded and spent (RunnerTypes.RoadParams). Raw quote units / bps. */
export interface RoadParams {
  runBudget: bigint;
  minRunBudget: bigint;
  opsProvision: bigint;
  waterSliceBps: bigint;
  overheatSliceBps: bigint;
  overheatBiasSteps: bigint;
  overheatCap: bigint;
}

/** Five-bucket waterfall of net project revenue R, basis points of R (RunnerTypes.WaterfallPolicy). */
export interface WaterfallPolicy {
  holdersBps: bigint;
  forgeBps: bigint;
  roadBps: bigint;
  prizeBps: bigint;
  opsBps: bigint;
  holderDeferredBps: bigint;
}

export interface RunnerState {
  x: bigint;
  y: bigint;
  vx: bigint;
  pressure: bigint;
  route: number;
  inSplit: boolean;
  slowPct: bigint;
  slowSteps: bigint;
  overheatSteps: bigint;
  stumbleUntilY: bigint;
}

export interface Crossing {
  index: number;
  kind: number;
  hit: boolean;
  xCross: bigint;
}

export interface StepOutput {
  state: RunnerState;
  pace: bigint;
  u: bigint;
  cursor: number;
  crossings: Crossing[];
}

export function initialState(): RunnerState {
  return {
    x: 0n,
    y: 0n,
    vx: 0n,
    pressure: 0n,
    route: Route.NEUTRAL,
    inSplit: false,
    slowPct: 0n,
    slowSteps: 0n,
    overheatSteps: 0n,
    stumbleUntilY: 0n,
  };
}
